/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import * as url from "helper/UrlHelper";
import { putData } from "utils/Gateway";

// ─────────────────────────────────────────────────────────────────────────────
// AutomationTemplateEditPopup
//
// TOKEN CLARIFICATION:
//   • automation.token  → "Webhook Auth Token" – the API key / Bearer token of
//     the RECIPIENT app (e.g. n8n).  Lobees sends this as
//     `Authorization: Bearer <token>` when calling the n8n webhook.
//
//   • payload_template.token (if present as a custom field) → the LOGGED-IN
//     USER's Lobees session token.  It travels inside the POST body so the
//     receiving workflow can call back into Lobees on behalf of that user.
//     This field is injected automatically at task-execution time and should
//     NOT be edited manually here.
// ─────────────────────────────────────────────────────────────────────────────
const AutomationTemplateEditPopup = ({ template, isOpen, onClose, onSaved }) => {
  const { t } = useTranslation();

  // ── Local form state ────────────────────────────────────────────────────────
  const [name, setName]               = useState("");
  const [description, setDescription] = useState("");
  const [executiontype, setExecutiontype] = useState("reactive");
  const [ispublic, setIspublic]       = useState(false);
  const [status, setStatus]           = useState("active");

  const [taskblueprintTitle, setTaskblueprintTitle]       = useState("");
  const [taskblueprintDesc,  setTaskblueprintDesc]        = useState("");

  // subtaskblueprint: array of subtask draft objects
  const [subtasks, setSubtasks] = useState([]);

  const [isSaving,   setIsSaving]   = useState(false);
  const [saveError,  setSaveError]  = useState("");
  const [saveSuccess, setSaveSuccess] = useState("");

  // ── Seed state from prop when popup opens ──────────────────────────────────
  useEffect(() => {
    if (!isOpen || !template) return;

    setName(template.name || "");
    setDescription(template.description || "");
    setExecutiontype(template.executiontype || "reactive");
    setIspublic(!!template.ispublic);
    setStatus(template.status || "active");
    setTaskblueprintTitle(template.taskblueprint?.title || "");
    setTaskblueprintDesc(template.taskblueprint?.description || "");
    setSaveError("");
    setSaveSuccess("");

    // Deep-clone subtasks so we don't mutate prop
    const cloned = (template.subtaskblueprint || [])
      .slice()
      .sort((a, b) => (a.step || 0) - (b.step || 0))
      .map((st) => ({
        _id:         st._id || "",
        title:       st.title || "",
        description: st.description || "",
        step:        st.step ?? 0,
        type:        st.type || "automation",
        automation: {
          provider:        st.automation?.provider || "n8n",
          // Recipient app's auth token (sent as Authorization: Bearer <token>)
          token:           st.automation?.token || "",
          webhook_url:     st.automation?.webhook_url || "",
          webhook_name:    st.automation?.webhook_name || "",
          // Extra payload fields stored as a JSON string for editing in the
          // textarea; parsed back to an object on save.
          payload_template: (() => {
            const pt = st.automation?.payload_template;
            if (!pt || (typeof pt === "object" && Object.keys(pt).length === 0)) return "";
            return typeof pt === "string" ? pt : JSON.stringify(pt, null, 2);
          })(),
        },
      }));
    setSubtasks(cloned);
  }, [isOpen, template]);

  if (!isOpen || !template) return null;

  // ── Subtask field updater ──────────────────────────────────────────────────
  const updateSubtask = (idx, field, value) => {
    setSubtasks((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const updateSubtaskAutomation = (idx, field, value) => {
    setSubtasks((prev) => {
      const next = [...prev];
      next[idx] = {
        ...next[idx],
        automation: { ...next[idx].automation, [field]: value },
      };
      return next;
    });
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaveError("");
    setSaveSuccess("");

    // Validate payload JSON for each subtask
    for (let i = 0; i < subtasks.length; i++) {
      const raw = (subtasks[i].automation.payload_template || "").trim();
      if (raw) {
        try { JSON.parse(raw); }
        catch {
          setSaveError(t(`Subtask #${i + 1}: Payload is not valid JSON.`));
          return;
        }
      }
    }

    setIsSaving(true);
    try {
      const builtSubtasks = subtasks.map((st) => {
        const raw = (st.automation.payload_template || "").trim();
        let payload_template = {};
        if (raw) {
          try { payload_template = JSON.parse(raw); } catch { payload_template = {}; }
        }
        return {
          _id:         st._id || undefined,
          title:       st.title,
          description: st.description,
          step:        Number(st.step) || 0,
          type:        st.type,
          automation: {
            provider:    st.automation.provider,
            token:       st.automation.token,
            webhook_url: st.automation.webhook_url,
            webhook_name: st.automation.webhook_name,
            payload_template,
          },
        };
      });

      const requestUrl =
        url.API_BASE_URL +
        url.API_UPDATE_AUTOMATION_TEMPLATE +
        `?templateid=${template._id}`;

      const body = {
        templateid:      template._id,
        name,
        description,
        executiontype,
        ispublic,
        status,
        taskblueprint:   { title: taskblueprintTitle, description: taskblueprintDesc },
        subtaskblueprint: builtSubtasks,
      };

      const response = await putData(requestUrl, body);

      if (!response) {
        setSaveError(t("Request failed — check your connection or session."));
      } else if (response.status) {
        setSaveSuccess(t("Template saved successfully."));
        onSaved && onSaved(response.data);
      } else {
        setSaveError(response.message || t("Failed to save template."));
      }
    } catch (err) {
      setSaveError(err.message || t("An error occurred."));
    }
    setIsSaving(false);
  };

  const isReactive = executiontype === "reactive";

  return (
    <>
      <div
        className="offcanvas offcanvas-end bg-white border-0 shadow-lg show"
        style={{ visibility: "visible", width: "80vw", maxWidth: "960px" }}
        tabIndex="-1"
      >
        {/* ── Header ── */}
        <div className="offcanvas-header bg-warning text-dark p-4 border-bottom">
          <div className="d-flex align-items-center gap-3">
            <div
              className="p-2 rounded-circle d-flex align-items-center justify-content-center"
              style={{ background: "rgba(0,0,0,0.08)" }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: "22px" }}>edit</span>
            </div>
            <div>
              <h5 className="offcanvas-title fw-bold mb-1">{t("Edit Template")}</h5>
              <small className="opacity-75">{template.name}</small>
            </div>
          </div>
          <button
            type="button"
            className="btn-close"
            onClick={onClose}
            aria-label="Close"
          />
        </div>

        {/* ── Body ── */}
        <div className="offcanvas-body p-4" style={{ overflowY: "auto" }}>

          {/* ── 1. Template Metadata ── */}
          <div className="mb-4 p-4 rounded-3 border shadow-sm bg-light">
            <div className="d-flex align-items-center gap-2 mb-3">
              <span className="material-symbols-outlined text-primary" style={{ fontSize: "20px" }}>info</span>
              <h6 className="mb-0 fw-bold">{t("Template Info")}</h6>
            </div>

            <div className="row g-3">
              <div className="col-12">
                <label className="form-label fw-semibold mb-1">{t("Template Name")} <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className="form-control"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              <div className="col-12">
                <label className="form-label fw-semibold mb-1">{t("Description")}</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

              <div className="col-md-4 col-6">
                <label className="form-label fw-semibold mb-1">{t("Execution Type")}</label>
                <select
                  className="form-select"
                  value={executiontype}
                  onChange={(e) => setExecutiontype(e.target.value)}
                >
                  <option value="reactive">{t("Reactive")}</option>
                  <option value="proactive">{t("Proactive")}</option>
                </select>
              </div>

              <div className="col-md-4 col-6">
                <label className="form-label fw-semibold mb-1">{t("Visibility")}</label>
                <select
                  className="form-select"
                  value={ispublic ? "true" : "false"}
                  onChange={(e) => setIspublic(e.target.value === "true")}
                >
                  <option value="false">{t("Private")}</option>
                  <option value="true">{t("Public")}</option>
                </select>
              </div>

              <div className="col-md-4 col-6">
                <label className="form-label fw-semibold mb-1">{t("Status")}</label>
                <select
                  className="form-select"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="active">{t("Active")}</option>
                  <option value="inactive">{t("Inactive")}</option>
                </select>
              </div>
            </div>
          </div>

          {/* ── 2. Parent Task Blueprint ── */}
          <div className="mb-4 p-4 rounded-3 border shadow-sm bg-white">
            <div className="d-flex align-items-center gap-2 mb-3">
              <span className="material-symbols-outlined text-primary" style={{ fontSize: "20px" }}>account_tree</span>
              <h6 className="mb-0 fw-bold">{t("Parent Task Blueprint")}</h6>
            </div>
            <div className="row g-3">
              <div className="col-12">
                <label className="form-label fw-semibold mb-1">{t("Title")}</label>
                <input
                  type="text"
                  className="form-control"
                  value={taskblueprintTitle}
                  onChange={(e) => setTaskblueprintTitle(e.target.value)}
                />
              </div>
              <div className="col-12">
                <label className="form-label fw-semibold mb-1">{t("Description")}</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={taskblueprintDesc}
                  onChange={(e) => setTaskblueprintDesc(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* ── 3. Subtask Blueprints ── */}
          {subtasks.length > 0 && (
            <div>
              <div className="d-flex align-items-center gap-2 mb-3">
                <span className="material-symbols-outlined text-primary" style={{ fontSize: "20px" }}>list_alt</span>
                <h6 className="mb-0 fw-bold">{t("Subtask Blueprints")}</h6>
                <span className="badge bg-secondary ms-auto">{subtasks.length} {t("subtasks")}</span>
              </div>

              {subtasks.map((st, idx) => (
                <div key={st._id || idx} className="mb-4 border rounded-3 bg-white shadow-sm overflow-hidden">

                  {/* Subtask strip header */}
                  <div className="d-flex align-items-center gap-2 px-3 py-2 bg-light border-bottom">
                    <span className="badge bg-primary">#{idx + 1}</span>
                    <span className="badge bg-secondary">{t("Step")} {st.step}</span>
                    <span className={`badge ${isReactive ? "bg-info text-dark" : "bg-warning text-dark"}`}>
                      {isReactive ? t("Reactive") : t("Proactive")}
                    </span>
                    <span className="fw-semibold text-dark ms-1 flex-grow-1">{st.title || t("Untitled subtask")}</span>
                  </div>

                  <div className="p-3">
                    {/* Basic fields */}
                    <div className="row g-3 mb-3">
                      <div className="col-md-6">
                        <label className="form-label fw-semibold mb-1" style={{ fontSize: "13px" }}>
                          {t("Title")}
                        </label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={st.title}
                          onChange={(e) => updateSubtask(idx, "title", e.target.value)}
                        />
                      </div>
                      <div className="col-md-3 col-6">
                        <label className="form-label fw-semibold mb-1" style={{ fontSize: "13px" }}>{t("Step")}</label>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={st.step}
                          min={0}
                          onChange={(e) => updateSubtask(idx, "step", e.target.value)}
                        />
                      </div>
                      <div className="col-md-3 col-6">
                        <label className="form-label fw-semibold mb-1" style={{ fontSize: "13px" }}>{t("Type")}</label>
                        <select
                          className="form-select form-select-sm"
                          value={st.type}
                          onChange={(e) => updateSubtask(idx, "type", e.target.value)}
                        >
                          <option value="automation">automation</option>
                          <option value="manual">manual</option>
                        </select>
                      </div>
                      <div className="col-12">
                        <label className="form-label fw-semibold mb-1" style={{ fontSize: "13px" }}>{t("Description")}</label>
                        <textarea
                          className="form-control form-control-sm"
                          rows={2}
                          value={st.description}
                          onChange={(e) => updateSubtask(idx, "description", e.target.value)}
                        />
                      </div>
                    </div>

                    {/* Automation config */}
                    <div
                      className="rounded-3 border overflow-hidden"
                      style={{ background: isReactive ? "rgba(13,202,240,0.04)" : "rgba(255,193,7,0.04)" }}
                    >
                      <div
                        className="px-3 py-2 border-bottom d-flex align-items-center gap-2"
                        style={{ background: isReactive ? "rgba(13,202,240,0.10)" : "rgba(255,193,7,0.10)" }}
                      >
                        <span className="material-symbols-outlined text-secondary" style={{ fontSize: "16px" }}>settings_ethernet</span>
                        <span className="fw-semibold text-dark" style={{ fontSize: "13px" }}>
                          {isReactive ? t("Webhook Config") : t("Payload Config")}
                        </span>
                      </div>

                      <div className="row g-0">
                        {/* Left: webhook config fields */}
                        <div className="col-xl-5 p-3 border-end">
                          {/* ── Token ── */}
                          <div className="mb-3">
                            <label className="form-label fw-semibold mb-1 d-flex align-items-center gap-1" style={{ fontSize: "13px" }}>
                              <span className="material-symbols-outlined" style={{ fontSize: "14px" }}>key</span>
                              {t("Token")}
                              <span className="badge bg-info text-dark ms-1" style={{ fontSize: "10px" }}>
                                {t("Recipient Verification")}
                              </span>
                            </label>
                            <input
                              type="text"
                              className="form-control form-control-sm font-monospace"
                              value={st.automation.token}
                              onChange={(e) => updateSubtaskAutomation(idx, "token", e.target.value)}
                              placeholder="e.g. n8n API key / Bearer token"
                              style={{ fontSize: "12px" }}
                            />
                            <small className="text-muted d-block mt-1" style={{ fontSize: "10px" }}>
                              {t("Sent as")} <code>Authorization: Bearer &lt;token&gt;</code> {t("— lets the recipient verify the request came from Lobees.")}
                            </small>
                          </div>

                          {/* ── Webhook URL ── */}
                          <div className="mb-3">
                            <label className="form-label fw-semibold mb-1 d-flex align-items-center gap-1" style={{ fontSize: "13px" }}>
                              <span className="material-symbols-outlined" style={{ fontSize: "14px" }}>link</span>
                              {t("Webhook URL")}
                            </label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={st.automation.webhook_url}
                              onChange={(e) => updateSubtaskAutomation(idx, "webhook_url", e.target.value)}
                              placeholder="https://your-n8n.com/webhook/..."
                              style={{ fontSize: "12px" }}
                            />
                          </div>

                          {/* ── Webhook Name ── */}
                          <div className="mb-0">
                            <label className="form-label fw-semibold mb-1 d-flex align-items-center gap-1" style={{ fontSize: "13px" }}>
                              <span className="material-symbols-outlined" style={{ fontSize: "14px" }}>label</span>
                              {t("Webhook Name")}
                            </label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={st.automation.webhook_name}
                              onChange={(e) => updateSubtaskAutomation(idx, "webhook_name", e.target.value)}
                              placeholder="e.g. send-email"
                              style={{ fontSize: "12px" }}
                            />
                          </div>
                        </div>

                        {/* Right: payload editor */}
                        <div className="col-xl-7 p-3 d-flex flex-column" style={{ background: "#fafafa" }}>
                          <label className="form-label fw-semibold mb-1 d-flex align-items-center gap-2" style={{ fontSize: "13px" }}>
                            <span className="material-symbols-outlined text-primary" style={{ fontSize: "15px" }}>data_object</span>
                            {t("Payload")}
                            <span className="badge bg-secondary ms-1" style={{ fontSize: "10px" }}>JSON</span>
                          </label>
                          <textarea
                            className="form-control form-control-sm font-monospace flex-grow-1"
                            rows={10}
                            value={st.automation.payload_template}
                            onChange={(e) => updateSubtaskAutomation(idx, "payload_template", e.target.value)}
                            placeholder={'{\n  "callbackUrl": "...",\n  "token": "<lobees-app-token>",\n  "tag": "value"\n}'}
                            style={{ fontSize: "12px", resize: "vertical" }}
                            spellCheck={false}
                          />
                          <div className="mt-2 p-2 rounded-2 border" style={{ background: "#e8f4fd", fontSize: "10px" }}>
                            <ul className="mb-0 ps-3" style={{ lineHeight: "1.8" }}>
                              <li>
                                {["{{taskid}}", "{{email}}", "{{name}}", "{{phone}}"].map((f) => (
                                  <code key={f} className="px-1 me-1 rounded" style={{ background: "#d0e8ff", color: "#0057a8" }}>{f}</code>
                                ))}
                                {t("— replaced with real lead data at execution time.")}
                              </li>
                              <li>
                                <code className="px-1 rounded" style={{ background: "#ffe0b2", color: "#7a4000" }}>token</code>
                                {" "}{t("— Lobees app token; n8n sends it back to authenticate its callback to the")}
                                {" "}<code>callbackUrl</code>.
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="offcanvas-header border-top bg-light gap-2">
          {/* Error / success message — left of Cancel */}
          <div className="flex-grow-1 me-2">
            {saveError && (
              <div className="d-flex align-items-center gap-1 text-danger" style={{ fontSize: "13px" }}>
                <span className="material-symbols-outlined flex-shrink-0" style={{ fontSize: "16px" }}>error</span>
                {saveError}
              </div>
            )}
            {saveSuccess && !saveError && (
              <div className="d-flex align-items-center gap-1 text-success" style={{ fontSize: "13px" }}>
                <span className="material-symbols-outlined flex-shrink-0" style={{ fontSize: "16px" }}>check_circle</span>
                {saveSuccess}
              </div>
            )}
          </div>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isSaving}>
            {t("Cancel")}
          </button>
          <button
            type="button"
            className="btn btn-primary d-flex align-items-center gap-2"
            onClick={handleSave}
            disabled={isSaving || !name.trim()}
          >
            {isSaving && <span className="spinner-border spinner-border-sm" />}
            <span className="material-symbols-outlined" style={{ fontSize: "18px" }}>save</span>
            {t("Save Changes")}
          </button>
        </div>
      </div>

      {/* Backdrop */}
      <div className="offcanvas-backdrop fade show" onClick={onClose} />
    </>
  );
};

export default AutomationTemplateEditPopup;
